import { BrowserRouter as Router, Route, Routes, useNavigate, useLocation } from 'react-router';
import React, { Suspense, useCallback, useEffect, useState, useRef } from 'react';
import { Toaster } from 'sonner';
import Preloader from 'components/loader/preloader';
import PrivateRoute from 'helpers/authHelper';
import NotFound from 'pages/not-found';
import NotAuthorized from 'pages/not-autorized';
import { getFirstAllowedRoute } from 'helpers/menuPermissionMap';
import ErrorBoundary from 'components/error-boundary';
import { AlertDialogProvider } from 'components/alert-dialog';

const Dashboard = React.lazy(() => import('pages/dashboard/index'));
const DashboardProfit = React.lazy(() => import('pages/dashboard/Profit/index'));
const DashboardMember = React.lazy(() => import('pages/dashboard/Transaksi/index'));
const KritikSaran = React.lazy(() => import('pages/customer-service/kritik-saran'));
const NotificationLists = React.lazy(() => import('pages/customer-service/notification'));
const NotificationDetails = React.lazy(() => import('pages/customer-service/notification/Detail'));
const Blacklist = React.lazy(() => import('pages/blacklist'));
const UserPage = React.lazy(() => import('pages/auth'));
const GoogleCallback = React.lazy(() => import('pages/auth/google-callback'));
const Content = React.lazy(() => import('pages/content/video'));
const TransferUang = React.lazy(() => import('pages/report/transfer-uang/list'));
const BlacklistIP = React.lazy(() => import('pages/blacklist-ip'));
const CustomerHistoryPin = React.lazy(() => import('pages/customer-history-pin/list'));
const CustomerEmoney = React.lazy(() => import('pages/report/customer-e-money/list'));
const CustomerProgram = React.lazy(() => import('pages/report/customer-program'));
const CustomerMarkupCompany = React.lazy(() => import('pages/report/customer-markup-company/list'));
const ListCategoryVoucher = React.lazy(() => import('pages/product-voucher/list-category'));
const ListProductVoucher = React.lazy(() => import('pages/product-voucher/list-product'));
const RegistrasiCustomer = React.lazy(() => import('pages/report/registrasi-customer'));

const CuankuAccount = React.lazy(() => import('pages/cuanku/account'));
const CuankuArea = React.lazy(() => import('pages/cuanku/area'));
const CuankuMargin = React.lazy(() => import('pages/cuanku/margin'));

const ProductPaketData = React.lazy(() => import('pages/ppob/product-paket-data'));
const Blog = React.lazy(() => import('pages/content/blog'));
const BlogAdd = React.lazy(() => import('pages/content/blog/add'));
const BlogDetail = React.lazy(() => import('pages/content/blog/detail'));
const UsersList = React.lazy(() => import('pages/user/list'));
const DeviceUser = React.lazy(() => import('pages/device-user/list'));
const UsersDetail = React.lazy(() => import('pages/user/detail'));
const UsersEdit = React.lazy(() => import('pages/user/edit'));
const UserPermissions = React.lazy(() => import('pages/user-permissions/index'));
const PermissionList = React.lazy(() => import('pages/user-permissions/list'));
const PermissionAdd = React.lazy(() => import('pages/user-permissions/add'));
const PermissionEdit = React.lazy(() => import('pages/user-permissions/edit'));
const IsimpleAccount = React.lazy(() => import('pages/isimple/Account/list'));
const TopupInbox = React.lazy(() => import('pages/web-topup/Inbox'));
const TopupTransaction = React.lazy(() => import('pages/web-topup/Transaction'));
const ListVoucher = React.lazy(() => import('pages/product-voucher/list-voucher'));
const ListMoneyTransferBank = React.lazy(() => import('pages/money-transfer-bank/list'));
const AddMoneyTransferBank = React.lazy(() => import('pages/money-transfer-bank/add'));
const EditMoneyTransferBank = React.lazy(() => import('pages/money-transfer-bank/edit'));

const EditVoucher = React.lazy(() => import('pages/product-voucher/list-voucher/edit'));
const CustomerPage = React.lazy(() => import('pages/customers'));
const PartnerPage = React.lazy(() => import('pages/partners'));
const PartnerDetailPage = React.lazy(() => import('pages/partners/detail'));
const PartnerAddPage = React.lazy(() => import('pages/partners/add'));
const CustomerDetailPage = React.lazy(() => import('pages/customers/detail'));
const ReportDeposit = React.lazy(() => import('pages/report/deposit'));
const SettingSPV = React.lazy(() => import('pages/setting/spv'));
const DigiposAccount = React.lazy(() => import('pages/digipos/account'));
const IsimpleArea = React.lazy(() => import('pages/isimple/Area/list'));
const ListCategoryInject = React.lazy(() => import('pages/product-inject/list-category'));
const InboxInject = React.lazy(() => import('pages/product-inject/inbox-inject/list'));
const DigiposArea = React.lazy(() => import('pages/digipos/area'));
const IsimpleMargin = React.lazy(() => import('pages/isimple/Margin/list'));
const VaDeposit = React.lazy(() => import('pages/setting/VaDeposit'));
const DigiposMargin = React.lazy(() => import('pages/digipos/margin'));
const MarkUpTransferUang = React.lazy(() => import('pages/setting/markUp-transferUang'));
const SettingTopUp = React.lazy(() => import('pages/setting/TopUp'));
const GlobalMarkup = React.lazy(() => import('pages/setting/markup-global/markupList'));
const CustomerVerifikasi = React.lazy(() => import('pages/customer-verifikasi'));
const DetailVerifikasi = React.lazy(() => import('pages/customer-verifikasi/detail'));
const Category = React.lazy(() => import('pages/content/category'));
const Otp = React.lazy(() => import('pages/otp'));
const SettingBankDeposit = React.lazy(() => import('pages/setting/bank-deposit/list'));
const PengajuanLimit = React.lazy(() => import('pages/request/pengajuan-limit'));
const LimitBypassEmoney = React.lazy(() => import('pages/ppob/limit-bypass-emoney'));
const EmoneyMarginSetting = React.lazy(() => import('pages/ppob/emoney-setting-margin'));

const EWalletDeposit = React.lazy(() => import('pages/e-wallet/deposit'));
const ListPpobVendor = React.lazy(() => import('pages/ppob/list-ppob-vendor'));
const DetailPPOBVendor = React.lazy(
  () => import('pages/ppob/list-ppob-vendor/detail/vendorDetail')
);
const BlockLimit = React.lazy(() => import('pages/setting/block-limit/limit'));
const SettinggsGeneral = React.lazy(() => import('pages/setting/general'));
const PromoPpob = React.lazy(() => import('pages/promo-ppob'));
const PromoUsed = React.lazy(() => import('pages/promo-ppob/used'));
const KuponDetail = React.lazy(() => import('pages/promo-ppob/detail'));
const EwalletExportTagihan = React.lazy(() => import('pages/e-wallet/export-tagihan'));
const Slider = React.lazy(() => import('pages/content/slider'));
const DetailSlider = React.lazy(() => import('pages/content/slider/component/detail'));
const SliderApp = React.lazy(() => import('pages/content/slider-app'));
const DetailSliderApp = React.lazy(() => import('pages/content/slider-app/component/detail'));
const CustomerMarkUp = React.lazy(() => import('pages/report/customer-markUp/list'));
const ListWhitelistSn = React.lazy(() => import('pages/product-inject/whitelist-sn'));
const Banner = React.lazy(() => import('pages/content/banner'));
const DetailBanner = React.lazy(() => import('pages/content/banner/component/detail'));
const MutasiBank = React.lazy(() => import('pages/e-wallet/mutasi-bank'));
const PPOBProductCategoryList = React.lazy(() => import('pages/ppob/category'));
const SaldoCustomerDeposit = React.lazy(() => import('pages/report/saldo-customer-deposit/list'));
const IdpelEmoney = React.lazy(() => import('pages/report/idpel-emoney'));
const IdpelTfBank = React.lazy(() => import('pages/report/idpel-tfbank'));
const MutasiSaldo = React.lazy(() => import('pages/report/mutasi-saldo'));
const PendingTime = React.lazy(() => import('pages/report/pending-time'));
const ContentTutorial = React.lazy(() => import('pages/content/tutorial/'));
const ContentStatis = React.lazy(() => import('pages/content/content-statis'));
const InputVoucherAdmin = React.lazy(() => import('pages/report/input-voucher-admin'));
const InnetTransaction = React.lazy(() => import('pages/report/innet-transaction'));
const InnetTransactionDetail = React.lazy(
  () => import('pages/report/innet-transaction/detail/detail')
);
const ReportPpobProductSale = React.lazy(() => import('pages/report/penjualan-product-ppob/'));
const ReportLabaRugiPPOB = React.lazy(() => import('pages/report/laba-rugi-ppob'));
const KomisiSPV = React.lazy(() => import('pages/report/komisi-spv'));
const TransferSaldo = React.lazy(() => import('pages/report/transfer-saldo'));
const CustomerFraud = React.lazy(() => import('pages/report/customer-fraud/list'));
const SettingsRekomenasiLimit = React.lazy(() => import('pages/setting/rekomendasi-limit'));
const PpobTransaction = React.lazy(() => import('pages/ppob-transaction'));
const PpobProductLabel = React.lazy(() => import('pages/ppob/product-label'));
const PpobTransactionDetailPage = React.lazy(() => import('pages/ppob-transaction/detail'));
const VideoDetail = React.lazy(() => import('pages/content/video/detail'));
const OmsetProduct = React.lazy(() => import('pages/report/omsetproduk/list'));
const DetailOmsetProduct = React.lazy(() => import('pages/report/omsetproduk/component/detail'));
const ReportPPOBTransaction = React.lazy(() => import('pages/report/transaksi-ppob'));
const OmsetCustomer = React.lazy(() => import('pages/report/omset-customer/list'));
const MonitorCusLimit = React.lazy(() => import('pages/monitoring-customer/limit'));
const WrongPin = React.lazy(() => import('pages/customer-wrong-pin'));
const PpobProduct = React.lazy(() => import('pages/ppob/product'));
const DetailPpobProduct = React.lazy(() => import('pages/ppob/product/detail'));
const ListProductVendor = React.lazy(() => import('pages/ppob/product-vendor/list'));
const ListProductVendorDetailPage = React.lazy(() => import('pages/ppob/product-vendor/detail'));
const CustomerQris = React.lazy(() => import('pages/report/customer-qris'));
const ReportWebtopupTransaction = React.lazy(() => import('pages/report/transaksi-webtopup'));
const ReportAppTransaction = React.lazy(() => import('pages/report/transaksi-app'));
const ChatLayout = React.lazy(() => import('pages/chat/index'));
const ChatGuestLayout = React.lazy(() => import('pages/chat-guest/index'));

const PaymentMethodDeposit = React.lazy(() => import('pages/e-wallet/pm-deposit'));
const PaymentMethodPPOB = React.lazy(() => import('pages/e-wallet/pm-transaction'));
const UpdatePmPPOB = React.lazy(() => import('pages/e-wallet/pm-transaction/components/update'));
const UpdatePmDeposit = React.lazy(() => import('pages/e-wallet/pm-deposit/components/update'));
const PaymentMethodWebTopup = React.lazy(() => import('pages/e-wallet/pm-webtopup'));
const UpdatePmWebTopup = React.lazy(() => import('pages/e-wallet/pm-webtopup/components/update'));

const PerformaOutlet = React.lazy(() => import('pages/report/performa-outlet'));
const TransactionInbox = React.lazy(() => import('pages/report/transaction-inbox'));

const TransaksiGroupTfsaldo = React.lazy(() => import('pages/report/transaksi-group-tfsaldo'));
const VoucherLabel = React.lazy(() => import('pages/ppob/voucher-label'));
const InjectLabel = React.lazy(() => import('pages/product-inject/inject-label'));
const PrefixProvider = React.lazy(() => import('pages/setting/prefix-provider'));

export const routesConfig = [
  // Dashboard
  { path: '/dashboard', name: 'Dashboard', component: Dashboard },
  { path: '/dashboard-profit', name: 'Dashboard Profit', component: DashboardProfit },
  { path: '/dashboard-trx', name: 'Dashboard Transaksi', component: DashboardMember },

  // Monitoring
  { path: '/blacklist-ip', name: 'Blacklist IP', component: BlacklistIP },
  { path: '/blacklist-customer', name: 'Blacklist Customer', component: Blacklist },
  {
    path: '/monitoring-customer-history-pin',
    name: 'Customer History PIN',
    component: CustomerHistoryPin,
  },
  {
    path: '/monitoring-customer-limit',
    name: 'Monitoring Customer Limit',
    component: MonitorCusLimit,
  },
  { path: '/monitoring-customer-wrong-pin', name: 'Customer Wrong PIN', component: WrongPin },
  { path: '/monitoring-otp', name: 'OTP', component: Otp },

  // Requests
  { path: '/request-limit', name: 'Pengajuan Limit', component: PengajuanLimit },

  // Customer Service
  { path: '/feedback', name: 'Kritik Saran', component: KritikSaran },
  { path: '/notifications', name: 'Notifications', component: NotificationLists },

  // Cuanku
  { path: '/cuanku-account', name: 'Cuanku Account', component: CuankuAccount },
  { path: '/cuanku-area', name: 'Cuanku Area', component: CuankuArea },
  { path: '/cuanku-margin', name: 'Cuanku Margin', component: CuankuMargin },

  // Members
  { path: '/customers', name: 'Customers', component: CustomerPage },
  { path: '/verification-customer', name: 'Verifikasi Customer', component: CustomerVerifikasi },
  { path: '/partners', name: 'Partners', component: PartnerPage },
  { path: '/partners/detail/:id', component: PartnerDetailPage },
  { path: '/partners/add', component: PartnerAddPage },

  // Promotions
  { path: '/ppob-promo', name: 'PPOB Promo', component: PromoPpob },

  // PPOB
  {
    path: '/ppob-product-category',
    name: 'PPOB Product Category',
    component: PPOBProductCategoryList,
  },
  { path: '/money-transfer-bank', name: 'Money Transfer Bank', component: ListMoneyTransferBank },
  {
    path: '/money-transfer-bank/add',
    name: 'Tambah Transfer Bank',
    component: AddMoneyTransferBank,
  },
  { path: '/ppob-vendor', name: 'PPOB Vendor', component: ListPpobVendor },
  { path: '/limit-bypass-emoney', name: 'Limit Bypass Emoney', component: LimitBypassEmoney },
  { path: '/emoney-setting-margin', name: 'Emoney Margin Setting', component: EmoneyMarginSetting },
  { path: '/ppob-product', name: 'PPOB Product', component: PpobProduct },
  { path: '/ppob-transaction', name: 'PPOB Transaction', component: PpobTransaction },
  { path: '/data-package-label', name: 'Data Package Label', component: PpobProductLabel },
  { path: '/product-inject-inbox', name: 'Product Inject Inbox', component: InboxInject },
  {
    path: '/product-inject-category',
    name: 'Product Inject Category',
    component: ListCategoryInject,
  },
  {
    path: '/product-inject-whitelist-sn',
    name: 'Product Inject Whitelist SN',
    component: ListWhitelistSn,
  },
  {
    path: '/product-paket-data-category',
    name: 'Product Paket Data Category',
    component: ProductPaketData,
  },
  {
    path: '/product-voucher-category',
    name: 'Product Voucher Category',
    component: ListCategoryVoucher,
  },
  {
    path: '/product-voucher-product',
    name: 'Product Voucher Product',
    component: ListProductVoucher,
  },
  {
    path: '/product-voucher-list-voucher',
    name: 'Product Voucher List Voucher',
    component: ListVoucher,
  },

  // Web Topup
  { path: '/topup-inbox', name: 'Topup Inbox', component: TopupInbox },
  { path: '/topup-transaction', name: 'Topup Transaction', component: TopupTransaction },

  // Digipos
  { path: '/digipos-account', name: 'Digipos Account', component: DigiposAccount },
  { path: '/digipos-area', name: 'Digipos Area', component: DigiposArea },
  { path: '/digipos-margin', name: 'Digipos Margin', component: DigiposMargin },

  // Isimple
  { path: '/isimple-account', name: 'Isimple Account', component: IsimpleAccount },
  { path: '/isimple-area', name: 'Isimple Area', component: IsimpleArea },
  { path: '/isimple-margin', name: 'Isimple Margin', component: IsimpleMargin },

  // Wallet
  { path: '/deposit', name: 'Deposit', component: EWalletDeposit },
  { path: '/export-tagihan', name: 'Export Tagihan', component: EwalletExportTagihan },
  { path: '/mutasi-bank', name: 'Mutasi Bank', component: MutasiBank },
  {
    path: '/payment-method-deposit',
    name: 'Payment Method Deposit',
    component: PaymentMethodDeposit,
  },
  { path: '/payment-method-ppob', name: 'Payment Method PPOB', component: PaymentMethodPPOB },
  {
    path: '/payment-method-webtopup',
    name: 'Payment Method WebTopup',
    component: PaymentMethodWebTopup,
  },

  // Content
  { path: '/content-banner', name: 'Banner', component: Banner },
  { path: '/content-blog', name: 'Blog', component: Blog },
  { path: '/blog/add', name: 'Tambah Blog', component: BlogAdd },
  { path: '/static-content', name: 'Static Content', component: ContentStatis },
  { path: '/content-category', name: 'Content Category', component: Category },
  { path: '/content-tutorial', name: 'Content Tutorial', component: ContentTutorial },
  { path: '/content-slider', name: 'Content Slider', component: Slider },
  { path: '/content-slider-app', name: 'Content Slider App', component: SliderApp },
  { path: '/content-video', name: 'Content Video', component: Content },

  // Settings
  { path: '/setting-general', name: 'General Settings', component: SettinggsGeneral },
  {
    path: '/setting-rekomenasi-limit',
    name: 'Rekomendasi Limit',
    component: SettingsRekomenasiLimit,
  },
  { path: '/setting-spv', name: 'SPV Settings', component: SettingSPV },
  { path: '/setting-va-deposit', name: 'VA Deposit', component: VaDeposit },
  { path: '/setting-mark-up', name: 'Mark Up Transfer Uang', component: MarkUpTransferUang },
  { path: '/setting-topup', name: 'TopUp Settings', component: SettingTopUp },
  { path: '/setting-markup-global', name: 'Global Markup', component: GlobalMarkup },
  { path: '/setting-bank-deposit', name: 'Bank Deposit', component: SettingBankDeposit },
  { path: '/setting-block-limit', name: 'Block Limit', component: BlockLimit },
  { path: '/prefix-provider', name: 'Prefix Provider', component: PrefixProvider },

  // Report
  { path: '/report-customer-fraud', name: 'Customer Fraud', component: CustomerFraud },
  { path: '/report-idpel-tfbank', name: 'IDPEL TF Bank', component: IdpelTfBank },
  { path: '/report-idpel-emoney', name: 'IDPEL Emoney', component: IdpelEmoney },
  { path: '/report-customer-emoney', name: 'Customer Emoney', component: CustomerEmoney },
  { path: '/report-customer-program', name: 'Customer Program', component: CustomerProgram },
  { path: '/report-customer-qris', name: 'Customer QRIS', component: CustomerQris },
  { path: '/report-ppob-pending-time', name: 'PPOB Pending Time', component: PendingTime },
  { path: '/report-innet-trx', name: 'Innet Transaction', component: InnetTransaction },
  {
    path: '/report-registrasi-customer',
    name: 'Registrasi Customer',
    component: RegistrasiCustomer,
  },
  {
    path: '/report-input-voucher-admin',
    name: 'Input Voucher Admin',
    component: InputVoucherAdmin,
  },
  { path: '/report-komisi-spv', name: 'Komisi SPV', component: KomisiSPV },
  { path: '/report-transfer-saldo', name: 'Transfer Saldo', component: TransferSaldo },
  { path: '/report-transfer-uang', name: 'Transfer Uang', component: TransferUang },
  { path: '/report-customer-markup', name: 'Customer Markup', component: CustomerMarkUp },
  { path: '/report-deposit', name: 'Report Deposit', component: ReportDeposit },
  {
    path: '/report-customer-markup-company',
    name: 'Customer Markup Company',
    component: CustomerMarkupCompany,
  },
  {
    path: '/report-customer-deposit-saldo',
    name: 'Customer Deposit Saldo',
    component: SaldoCustomerDeposit,
  },
  { path: '/report-ppob-sale', name: 'PPOB Sale', component: ReportPpobProductSale },
  { path: '/report-mutasi-saldo', name: 'Mutasi Saldo', component: MutasiSaldo },
  { path: '/report-omset-product', name: 'Omset Product', component: OmsetProduct },
  { path: '/report/transaction-ppob', name: 'Transaction PPOB', component: ReportPPOBTransaction },
  {
    path: '/monitoring-customer-limit',
    name: 'Monitoring Customer Limit',
    component: MonitorCusLimit,
  },
  { path: '/ppob/product', name: 'PPOB Product', component: PpobProduct },
  { path: '/product-vendor', name: 'Product Vendor', component: ListProductVendor },
  { path: '/report-laba-rugi-ppob', name: 'Laba Rugi PPOB', component: ReportLabaRugiPPOB },
  { path: '/report-customer-omset', name: 'Customer Omset', component: OmsetCustomer },
  {
    path: '/report-ppob-transaction',
    name: 'Report PPOB Transaction',
    component: ReportPPOBTransaction,
  },
  {
    path: '/report-webtopup-transaction',
    name: 'Report Webtopup Transaction',
    component: ReportWebtopupTransaction,
  },
  {
    path: '/report-app-transaction',
    name: 'Report App Transaction',
    component: ReportAppTransaction,
  },

  // Chat
  { path: '/chat', name: 'Chat Customer', component: ChatLayout },
  { path: '/chat-guest', name: 'Chat Guest', component: ChatGuestLayout },

  // Users
  { path: '/user', name: 'Users', component: UsersList },
  { path: '/device-user', name: 'Device User', component: DeviceUser },
  // Performa
  { path: '/report-performa-outlet', name: 'Performa Outlet', component: PerformaOutlet },

  // Transaction
  { path: '/trx-group-tfsaldo', name: 'Transaksi Group TFSaldo', component: TransaksiGroupTfsaldo },
  { path: '/report-transaction-inbox', name: 'Transaction Inbox', component: TransactionInbox },

  // Labels
  { path: '/voucher-label', name: 'Voucher Label', component: VoucherLabel },
  { path: '/inject-label', name: 'Inject Label', component: InjectLabel },
];

export const dynamicRoutes = [
  { path: '/users/detail/:id', component: UsersDetail },
  { path: '/users/edit/:id', component: UsersEdit },
  { path: '/notifications/detail/:id', component: NotificationDetails },
  { path: '/customers/detail/:id', component: CustomerDetailPage },
  { path: '/verifikasi/detail/:id', component: DetailVerifikasi },
  { path: '/kupon/used/:id', component: PromoUsed },
  { path: '/kupon/detail/:id', component: KuponDetail },
  { path: '/money-transfer-bank/detail/:id', component: EditMoneyTransferBank },
  { path: '/ppob/product/detail/:id', component: DetailPpobProduct },
  { path: '/list-ppob-vendor/detail/:id', component: DetailPPOBVendor },
  { path: '/ppob-transaction/detail/:id', component: PpobTransactionDetailPage },
  { path: '/list-voucher/edit/:id', component: EditVoucher },
  { path: '/payment-method-ppob/detail/:id', component: UpdatePmPPOB },
  { path: '/payment-method-deposit/detail/:id', component: UpdatePmDeposit },
  { path: '/payment-method-webtopup/detail/:id', component: UpdatePmWebTopup },
  { path: '/banner/detail/:id', component: DetailBanner },
  { path: '/blog/detail/:id', component: BlogDetail },
  { path: '/content-slider-app/detail/:id', component: DetailSliderApp },
  { path: '/slider/detail/:id', component: DetailSlider },
  { path: '/video/detail/:id', component: VideoDetail },
  { path: '/innet-transaction/detail/:id', component: InnetTransactionDetail },
  { path: '/report/report-detail-omset/:id', component: DetailOmsetProduct },
  { path: '/report/report-detail-omset/:id/:cat_id', component: DetailOmsetProduct },
  { path: '/report/report-detail-omset/:id/:start_date/:end_date/', component: DetailOmsetProduct },
  {
    path: '/report/report-detail-omset/:id/:start_date/:end_date/:cat_id',
    component: DetailOmsetProduct,
  },
  { path: '/product-vendor/detail/:id', component: ListProductVendorDetailPage },
];

const getCategoryFromPath = (path) => {
  const menuStructure = {
    Dashboard: ['/dashboard', '/dashboard-profit', '/dashboard-trx'],
    Monitoring: [
      '/blacklist-customer',
      '/blacklist-ip',
      '/monitoring-otp',
      '/monitoring-customer-limit',
      '/monitoring-customer-history-pin',
      '/monitoring-customer-wrong-pin',
    ],
    'Customer Service': ['/chat', '/chat-guest', '/feedback', '/notifications'],
    Members: ['/customers', '/setting-spv', '/partners'],
    Pengajuan: ['/verification-customer', '/request-limit'],
    'E-Wallet': [
      '/deposit',
      '/payment-method-deposit',
      '/payment-method-ppob',
      '/payment-method-webtopup',
      '/setting-bank-deposit',
    ],
    PPOB: [
      '/ppob-product-category',
      '/ppob-product',
      '/ppob-vendor',
      '/product-vendor',
      '/product-inject-category',
      '/inject-label',
      '/product-inject-whitelist-sn',
      '/product-paket-data-category',
      '/data-package-label',
      '/product-voucher-category',
      '/voucher-label',
      '/product-voucher-product',
      '/product-voucher-list-voucher',
      '/digipos-account',
      '/digipos-area',
      '/digipos-margin',
      '/isimple-account',
      '/isimple-area',
      '/isimple-margin',
      '/cuanku-account',
      '/cuanku-area',
      '/cuanku-margin',
      '/money-transfer-bank',
      '/setting-markup-global',
      '/setting-mark-up',
      '/limit-bypass-emoney',
      '/emoney-setting-margin',
    ],
    'PPOB TRX': [
      '/ppob-transaction',
      '/report-transaction-inbox',
      '/product-inject-inbox',
      '/report-innet-trx',
      '/report-transfer-uang',
      '/report-transfer-saldo',
    ],
    Konten: [
      '/content-banner',
      '/content-blog',
      '/content-category',
      '/content-slider',
      '/content-slider-app',
      '/content-video',
      '/content-tutorial',
      '/static-content',
    ],
    Reports: [
      '/report-customer-deposit-saldo',
      '/report-deposit',
      '/report-mutasi-saldo',
      '/report-registrasi-customer',
      '/report-customer-fraud',
      '/report-customer-markup-company',
      '/report-customer-markup',
      '/report-customer-program',
      '/report-customer-qris',
      '/report-idpel-emoney',
      '/trx-group-tfsaldo',
      '/report-idpel-tfbank',
      '/report-ppob-pending-time',
      '/report-ppob-transaction',
      '/report-webtopup-transaction',
      '/report-app-transaction',
      '/report-customer-omset',
      '/report-omset-product',
      '/report-komisi-spv',
      '/report-ppob-sale',
      '/report-performa-outlet',
      '/report-customer-emoney',
      '/report-laba-rugi-ppob',
      '/report-input-voucher-admin',
      '/export-tagihan',
      '/mutasi-bank',
    ],
    Pengaturan: [
      '/setting-general',
      '/user',
      '/user-permissions',
      '/device-user',
      '/prefix-provider',
      '/setting-block-limit',
      '/setting-rekomenasi-limit',
    ],
  };

  for (const [category, paths] of Object.entries(menuStructure)) {
    // Check if the current path starts with any of the defined paths for this category
    // We use startsWith to handle dynamic routes or sub-paths if necessary,
    // but exact match is preferred for the main menu items.
    if (paths.some((p) => path === p || path.startsWith(p + '/'))) {
      return category;
    }
  }

  return 'Others';
};

export const getSearchableRoutes = () => {
  return routesConfig.map((route) => ({
    path: route.path,
    name: route.name,
    category: getCategoryFromPath(route.path),
  }));
};

const AuthChecker = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const Auth = localStorage.getItem('Pulsalink-Admin-Auth');
    const AuthKey = localStorage.getItem('Pulsalink-Admin-Auth-Key');
    const AuthUser = localStorage.getItem('Pulsalink-Admin-Auth-User');

    if (Auth !== null && AuthKey !== null && AuthUser !== null) {
      try {
        const user = JSON.parse(AuthUser);
        const permissions = user?.permissions || [];
        const userLevel = user?.user_level;
        const isOwner = userLevel && String(userLevel).toLowerCase() === 'owner';

        if (isOwner) {
          navigate('/dashboard');
          return;
        }

        // Check allowed routes based on permissions
        const firstAllowedRoute = getFirstAllowedRoute(permissions);

        if (firstAllowedRoute) {
          navigate(firstAllowedRoute);
        } else {
          // No allowed routes - redirect to not-authorized
          navigate('/not-authorized');
        }
      } catch {
        // Failed to parse user - go to login
        navigate('/user/login');
      }
    } else {
      navigate('/user/login');
    }
  }, [navigate]);

  return null;
};

const RouteLoadingOverlay = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const targetPath = useRef(null);
  const timerRef = useRef(null);

  const showLoading = useCallback(() => {
    targetPath.current = window.location.pathname + window.location.search;
    setLoading(true);
  }, []);

  useEffect(() => {
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      const result = origPush.apply(this, args);
      showLoading();
      return result;
    };
    window.history.replaceState = function (...args) {
      const result = origReplace.apply(this, args);
      showLoading();
      return result;
    };
    window.addEventListener('popstate', showLoading);
    return () => {
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
      window.removeEventListener('popstate', showLoading);
    };
  }, [showLoading]);

  useEffect(() => {
    if (!loading) return;
    const current = location.pathname + location.search;
    if (targetPath.current === current) {
      setLoading(false);
      targetPath.current = null;
    } else {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setLoading(false);
        targetPath.current = null;
      }, 10000);
    }
    return () => clearTimeout(timerRef.current);
  }, [loading, location.pathname, location.search]);

  if (!loading) return null;
  return <Preloader />;
};

const AppMain = () => {
  return (
    <ErrorBoundary>
      <div className='w-full font-public'>
        <Toaster position='top-right' richColors closeButton />
        <AlertDialogProvider />
        <Suspense fallback={<Preloader />}>
          <Router>
            <RouteLoadingOverlay />
            <Routes>
              <Route path='/' element={<AuthChecker />} />
              <Route path='/user/*' element={<UserPage />} />
              <Route path='/auth/google/callback' element={<GoogleCallback />} />
              <Route element={<PrivateRoute />}>
                <Route path='/user-permissions' element={<UserPermissions />}>
                  <Route index element={<PermissionList />} />
                  <Route path='add' element={<PermissionAdd />} />
                  <Route path='edit/:id' element={<PermissionEdit />} />
                </Route>
                <Route path='/not-authorized' element={<NotAuthorized />} />
                <Route path='*' element={<NotFound />} />
                {routesConfig.map((route) => (
                  <Route key={route.path} path={route.path} element={<route.component />} />
                ))}
                {dynamicRoutes.map((route) => (
                  <Route key={route.path} path={route.path} element={<route.component />} />
                ))}
              </Route>
            </Routes>
          </Router>
        </Suspense>
      </div>
    </ErrorBoundary>
  );
};

export default AppMain;
